import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Typography, FormControlLabel, Switch, Tooltip, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { DataTable, EmptyState } from '@fluentsms/agentnet-web-components';
import { LocationEmptyState } from '@fluentsms/agentnet-web-icons';
import { useAuth } from '@agentnet/auth';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetDropdownSelector, AgentNetTextInput } from 'ui-kit/inputs';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { createFormApi, getFormDetailsApi, updateFormApi } from 'api/formManagement/form-management-api';
import { CreateFormModel, FormStateModel } from './types';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { FORM_TYPE_OPTIONS, COVERAGE_CLASS_OPTIONS, UNDERWRITER_OPTIONS, POLICY_TYPE_OPTIONS } from './constants';
import { createFormSchema } from 'utilities/validation/schemas/form-management-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { toDisplayDateString } from 'utilities/dateFormatter';
import AddStateDrawer from './AddStateDrawer';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(4),
  },
  statesSection: {
    marginTop: theme.spacing(4),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  statesSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statusToggle: {
    marginRight: theme.spacing(2),
  },
  gridWrapper: {
    marginTop: theme.spacing(2),
  },
  gridContainer: {
    height: '400px',
    width: '100%',
    fontFamily: theme.typography.fontFamily,
  },
  emptyStateWrapper: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(6, 2),
  },
  statesError: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.75),
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  disabledIcon: {
    color: '#ccc',
    cursor: 'not-allowed',
  },
  tooltip: {
    fontSize: '12px',
  },
  backArrow: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(-1),
    padding: theme.spacing(1),
  },
  titleWithArrow: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface CreateNewFormProps {
  onFormCreated: () => void;
  onCancel: () => void;
  isEditMode?: boolean;
  formIdentifier?: {
    starsItemNumber: string;
    starsItemType: string;
    underwriterCode: string;
  } | null;
}

const CreateNewForm: React.FC<CreateNewFormProps> = ({
  onFormCreated,
  onCancel,
  isEditMode = false,
  formIdentifier = null,
}) => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const { addSnackbarMessage } = useSnackBars();

  const [formData, setFormData] = useState<CreateFormModel>({
    formItemNo: '',
    formType: '',
    description: '',
    policyTypeDescription: '',
    starsProductTypeCode: '',
    coverageClass: '',
    underwriter: '',
    isActive: true,
    states: [],
  });

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isShowValidation, setIsShowValidation] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [stateToDelete, setStateToDelete] = useState<string>('');
  const [isAddStateDrawerOpen, setIsAddStateDrawerOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingState, setEditingState] = useState<FormStateModel | null>(null);
  const [isEditStateDrawerOpen, setIsEditStateDrawerOpen] = useState<boolean>(false);

  const validateFields = async () => {
    const errs = await doValidate(formData, createFormSchema);
    setValidationErrors(errs || []);
    setIsFormValid(!errs || errs.length === 0);
  };

  useEffect(() => {
    if (isShowValidation) {
      validateFields();
    }
  }, [formData, isShowValidation]);

  // Fetch form details in edit mode
  useEffect(() => {
    if (isEditMode && formIdentifier) {
      const fetchFormDetails = async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
          const token = await getAccessToken();
          const details = await getFormDetailsApi(
            formIdentifier.starsItemNumber,
            formIdentifier.starsItemType,
            formIdentifier.underwriterCode,
            token,
          );
          // Transform response to form data model
          setFormData({
            formItemNo: details.starsItemNumber,
            formType: details.starsItemType,
            description: details.formDescription,
            policyTypeDescription: details.policyTypeDescription || '',
            starsProductTypeCode: details.starsProductTypeCode || '',
            coverageClass: details.coverageClass || '',
            underwriter: details.underwriterCode || '',
            isActive: details.formStatus === 'Active',
            states: details.states.map((s) => ({
              state: s.stateCode,
              stateName: s.stateName,
              faccProductId: s.faccProductId?.toString() || '',
              faccExtProdId: s.faccExtendedProductId?.toString() || '',
              faccExtendedCoverageId: s.faccExtCoverageId?.toString() || '',
              activeDate: s.jacketEffectiveStartDate || '',
              inactiveDate: s.jacketEffectiveEndDate || '',
              eJacketsEnabled: s.isEJacketEnabled,
              hasBrandId: s.brandId === 1,
            })),
          });
        } catch (error) {
          setLoadError('Failed to load form details');
          addSnackbarMessage({ message: 'Failed to load form details', type: 'error' });
        } finally {
          setIsLoading(false);
        }
      };
      fetchFormDetails();
    }
  }, [isEditMode, formIdentifier]);

  const handleDeleteState = (stateCode: string) => {
    setStateToDelete(stateCode);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteState = () => {
    setFormData({
      ...formData,
      states: formData.states.filter((s) => s.state !== stateToDelete),
    });
    setShowDeleteConfirmation(false);
    setStateToDelete('');
  };

  const handleOpenAddStateDrawer = () => {
    setIsAddStateDrawerOpen(true);
  };

  const handleCloseAddStateDrawer = () => {
    setIsAddStateDrawerOpen(false);
  };

  const handleSaveStates = (newStates: FormStateModel[]) => {
    setFormData({
      ...formData,
      states: [...formData.states, ...newStates],
    });
  };

  const handleEditState = (state: FormStateModel) => {
    setEditingState(state);
    setIsEditStateDrawerOpen(true);
  };

  const handleCloseEditStateDrawer = () => {
    setIsEditStateDrawerOpen(false);
    setEditingState(null);
  };

  const handleSaveEditedState = (updatedState: FormStateModel) => {
    setFormData({
      ...formData,
      states: formData.states.map((s) => (s.state === updatedState.state ? updatedState : s)),
    });
    setIsEditStateDrawerOpen(false);
    setEditingState(null);
  };

  const handleSaveForm = async () => {
    if (isCreating) {
      return;
    }

    setIsShowValidation(true);
    const errs = await doValidate(formData, createFormSchema);
    setValidationErrors(errs || []);

    if (!errs || errs.length === 0) {
      setIsCreating(true);
      try {
        const token = await getAccessToken();

        const isJacket = formData.formType === 'Policy';
        const { policyTypeDescription, ...formDataWithoutPolicyType } = formData;
        const payload = isJacket ? formData : formDataWithoutPolicyType;
        let response;
        if (isEditMode && formIdentifier) {
          response = await updateFormApi(formData, formIdentifier, token);
        } else {
          response = await createFormApi(payload, token);
        }

        if (response.rowCount > 0) {
          addSnackbarMessage({
            message: isEditMode
              ? `Form ${formData.formItemNo} updated successfully.`
              : `Form ${formData.formItemNo} - ${formData.description} created successfully. ${response.rowCount} product item(s) created.`,
            type: 'success',
          });

          onFormCreated();
        } else {
          addSnackbarMessage({
            message: isEditMode ? 'Form update failed.' : 'Form creation failed. No items were created.',
            type: 'error',
          });
        }
      } catch (error) {
        addSnackbarMessage({
          message: isEditMode ? 'Failed to update form. Please try again.' : 'Failed to create form. Please try again.',
          type: 'error',
        });
      } finally {
        setIsCreating(false);
      }
    }
  };

  const stateGridColumns: ColDef[] = useMemo(
    () => [
      {
        headerName: 'State',
        field: 'state',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'FACC Product ID',
        field: 'faccProductId',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'FACC Ext Prod ID',
        field: 'faccExtProdId',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'FACC Extended Coverage ID',
        field: 'faccExtendedCoverageId',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: 'Active Date',
        field: 'activeDate',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        valueFormatter: (params: ValueFormatterParams) => toDisplayDateString(params.value),
      },
      {
        headerName: 'Inactive Date',
        field: 'inactiveDate',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        valueFormatter: (params: ValueFormatterParams) => toDisplayDateString(params.value),
      },
      {
        headerName: 'eJackets Enabled?',
        field: 'eJacketsEnabled',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        valueFormatter: (params: ValueFormatterParams) => (params.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Has Brand ID?',
        field: 'hasBrandId',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        suppressHeaderMenuButton: true,
        valueFormatter: (params: ValueFormatterParams) => (params.value ? 'Yes' : 'No'),
      },
      {
        headerName: '',
        field: 'edit',
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        cellRenderer: (params: ICellRendererParams) => (
          <div className={classes.iconContainer}>
            <Tooltip title="Edit State" classes={{ tooltip: classes.tooltip }}>
              <EditOutlinedIcon
                fontSize="small"
                htmlColor="#0074CA"
                onClick={() => handleEditState(params.data)}
                data-testid={`CreateNewForm-EditState-${params.data?.state}`}
              />
            </Tooltip>
          </div>
        ),
        width: 50,
        maxWidth: 50,
      },
      {
        headerName: '',
        field: 'delete',
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        cellRenderer: (params: ICellRendererParams) => (
          <div className={classes.iconContainer}>
            <Tooltip title="Delete State" classes={{ tooltip: classes.tooltip }}>
              <DeleteOutlineOutlinedIcon
                fontSize="small"
                htmlColor="#0074CA"
                onClick={() => handleDeleteState(params.data.state)}
              />
            </Tooltip>
          </div>
        ),
        width: 50,
        maxWidth: 50,
      },
    ],
    [classes],
  );

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <LoadingSpinner status="pending" />
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Box p={3}>
        <Typography color="error">{loadError}</Typography>
        <AgentNetButton onClick={onCancel} style={{ marginTop: 16 }}>
          Back to Form Management
        </AgentNetButton>
      </Box>
    );
  }

  // Dynamic header title for edit mode
  const getFormTypeDisplay = (type: string) => {
    if (type === 'POLICY' || type === 'Policy') return 'Jacket';
    return type;
  };

  const headerTitleText = isEditMode ? getEditHeaderTitle() : 'Create New Form';

  const headerTitle = (
    <Box className={classes.titleWithArrow}>
      <IconButton
        onClick={onCancel}
        className={classes.backArrow}
        data-testid="CreateNewForm-BackArrow"
        aria-label="Back to Form Management"
      >
        <ArrowBackIcon />
      </IconButton>
      <span>{headerTitleText}</span>
    </Box>
  );

  const getFormTypeOptions = (): Array<{ name: string; value: string }> => {
    if (FORM_TYPE_OPTIONS.findIndex((ft) => ft.value === formData.formType) >= 0) {
      return FORM_TYPE_OPTIONS;
    }
    return [...FORM_TYPE_OPTIONS, { name: formData.formType, value: formData.formType }];
  };

  return (
    <div>
      <PageHeader
        divider={true}
        title={headerTitle}
        contentRight={
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                color="primary"
                data-testid="CreateNewForm-StatusToggle"
              />
            }
            label={formData.isActive ? 'Active' : 'Inactive'}
            labelPlacement="start"
            className={classes.statusToggle}
          />
        }
      />
      <Box className={classes.container}>
        <div className={classes.formSection}>
          <Grid container spacing={3}>
            {/* Row 1 */}
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                fullWidth
                label="Form/Item No."
                name="formItemNo"
                value={formData.formItemNo}
                onChange={(e) => setFormData({ ...formData, formItemNo: e.target.value })}
                required
                showValidation={isShowValidation}
                errs={validationErrors}
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                fullWidth
                label="Form Type"
                name="formType"
                value={formData.formType}
                options={getFormTypeOptions()}
                menuOption={(value) => setFormData({ ...formData, formType: value, policyTypeDescription: '' })}
                required
                showValidation={isShowValidation}
                errs={validationErrors}
                disabled={isEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                showValidation={isShowValidation}
                errs={validationErrors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                fullWidth
                label="Policy Type"
                name="policyTypeDescription"
                value={formData.policyTypeDescription}
                options={POLICY_TYPE_OPTIONS}
                menuOption={(value) => setFormData({ ...formData, policyTypeDescription: value })}
                required={formData.formType === 'Policy'}
                disabled={formData.formType !== 'Policy'}
                showValidation={isShowValidation}
                errs={validationErrors}
              />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={3}>
              <AgentNetTextInput
                fullWidth
                label="STARS Product Type Code"
                name="starsProductTypeCode"
                value={formData.starsProductTypeCode}
                onChange={(e) => setFormData({ ...formData, starsProductTypeCode: e.target.value })}
                showValidation={isShowValidation}
                errs={validationErrors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                fullWidth
                label="Coverage Class"
                name="coverageClass"
                value={formData.coverageClass}
                options={COVERAGE_CLASS_OPTIONS}
                menuOption={(value) => setFormData({ ...formData, coverageClass: value })}
                required
                showValidation={isShowValidation}
                errs={validationErrors}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <AgentNetDropdownSelector
                fullWidth
                label="Underwriter"
                name="underwriter"
                value={formData.underwriter}
                options={UNDERWRITER_OPTIONS}
                menuOption={(value) => setFormData({ ...formData, underwriter: value })}
                required={formData.formType === 'Policy'}
                showValidation={isShowValidation}
                errs={validationErrors}
                disabled={isEditMode}
              />
            </Grid>
          </Grid>
        </div>

        {/* States Grid Section */}
        <Box className={classes.statesSection}>
          <Box className={classes.statesSectionHeader}>
            <Typography variant="h6" className={classes.sectionTitle}>
              States *
            </Typography>
            {formData.states.length > 0 && (
              <AgentNetButton
                variant="contained"
                color="primary"
                onClick={handleOpenAddStateDrawer}
                data-testid="CreateNewForm-AddStateButton"
                size="small"
              >
                + Add State
              </AgentNetButton>
            )}
          </Box>
          {isShowValidation && formData.states.length === 0 && (
            <Typography className={classes.statesError}>At least one state is required</Typography>
          )}
          {formData.states.length === 0 ? (
            <Box className={classes.emptyStateWrapper}>
              <EmptyState
                icon={<LocationEmptyState />}
                headerText="No States Added"
                descriptionText="Add at least one state to the form"
                primaryButtonProps={{
                  label: '+ Add State',
                  onClick: handleOpenAddStateDrawer,
                }}
              />
            </Box>
          ) : (
            <Box className={classes.gridWrapper}>
              <div className={`ag-theme-alpine table-grid ${classes.gridContainer}`}>
                <DataTable
                  columnDefs={stateGridColumns}
                  rowData={formData.states}
                  defaultColDef={{
                    flex: 1,
                    sortable: true,
                    resizable: true,
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    suppressFloatingFilterButton: true,
                    suppressHeaderFilterButton: true,
                    suppressHeaderMenuButton: true,
                    minWidth: 100,
                  }}
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit();
                  }}
                  overlayNoRowsTemplate="<span>No states configured</span>"
                />
              </div>
            </Box>
          )}
        </Box>
      </Box>

      <BottomAppBar
        primaryActionLabel={isEditMode ? 'Save' : 'Create Form'}
        cancelLabel="Cancel"
        onPrimaryClick={handleSaveForm}
        onCancel={onCancel}
        disablePrimary={(!isFormValid && isShowValidation) || isCreating}
        primaryButtonLoader={isCreating}
        cancelDataQa="CreateNewForm-Cancel"
        primaryActionDataQa={isEditMode ? 'CreateNewForm-SaveForm' : 'CreateNewForm-CreateForm'}
      />

      <AgentNetConfirmationDialog
        qaAttrPrefix="DeleteStateConfirmation"
        onConfirm={confirmDeleteState}
        open={showDeleteConfirmation}
        onDismissAction={() => setShowDeleteConfirmation(false)}
        dialogTitle="Delete State"
        dialogBtnContent="Yes, Delete"
        dialogText="Are you sure you want to remove this state from the form?"
      />

      <AddStateDrawer
        open={isAddStateDrawerOpen}
        onClose={handleCloseAddStateDrawer}
        onSave={handleSaveStates}
        formType={formData.formType}
        existingStates={formData.states}
      />

      <AddStateDrawer
        key={editingState ? `edit-${editingState.state}` : 'add'}
        open={isEditStateDrawerOpen}
        onClose={handleCloseEditStateDrawer}
        onSave={handleSaveStates}
        formType={formData.formType}
        existingStates={formData.states}
        editingState={editingState}
        onSaveEdit={handleSaveEditedState}
      />
    </div>
  );

  function getEditHeaderTitle() {
    const base = `Form ${formData.formItemNo} - ${formData.formType}`;
    return formData.description ? `${base} - ${formData.description}` : base;
  }
};

export default CreateNewForm;
